/**
 * What the run is doing while it generates.
 *
 * A generation takes one to two minutes, and "a loader and the same 'the AI is working through'" for
 * that long reads as a hung page (the PM's words). The page now keeps the wizard on screen while it
 * generates — the steps fill in as the worker reads — and this is the slim strip across the top of it:
 * the overall bar, what the worker is doing, the clocks and Stop. "Details" opens the full picture: the
 * phase timeline with how long each finished phase took, every document with its own pages, which of the
 * contract facts the AI has mentioned so far, and the AI's own words as it works.
 *
 * Nothing here is invented. Every figure comes from the run (`status`, `startedAt`, `typicalDurationMs`,
 * `documents`) or from the worker's live progress (CLE-2811, polled every 4 s by the page). The only
 * estimate is inside a phase with no natural count — the queue, the AI's modeling, saving — where the bar
 * eases towards the end of that phase's share against a stated expectation and never gets there until
 * the phase really ends (see `progress/progressModel.ts`). The bar never goes backwards within a pass.
 *
 * The worker's progress v2 fields (per-document pages, phase durations, narration, actions, turns) are
 * all optional: with a v1 worker the panel still shows the phase, the document in hand and the timers.
 */

import React, { useEffect, useId, useRef, useState } from "react";
import styled from "styled-components";

import { IDraftRunDetail } from "../../store/queries/contractModelDrafts";
import {
  PROGRESS_CHIP_QUEUED,
  PROGRESS_CHIP_SAVING,
  PROGRESS_TITLE_FACTS,
  PROGRESS_TITLE_FULL,
  usuallyAbout,
} from "./copy";
import DocumentList from "./progress/DocumentList";
import LiveFromAi from "./progress/LiveFromAi";
import LookingForChecklist from "./progress/LookingForChecklist";
import PhaseTimeline from "./progress/PhaseTimeline";
import {
  EMPTY_OBSERVATIONS,
  IMonotonicPercent,
  IPhaseObservations,
  accumulateTicks,
  activePhaseOf,
  activePhaseStartMs,
  activityOf,
  checklistFor,
  compactDuration,
  derivePhaseDurations,
  holdMonotonic,
  isReusePass,
  mergeDocuments,
  narrationOf,
  observeProgress,
  overallPercent,
  passKeyOf,
  phaseStartMs,
  tipAt,
  turnsOf,
} from "./progress/progressModel";
import ProgressStrip from "./progress/ProgressStrip";
import { fadeIn } from "./progress/styles";
import { useLiveProgress } from "./progress/useLiveProgress";
import {
  describeProgress,
  generationElapsedMs,
  isTakingLongerThanUsual,
} from "./utils";

/** The full panel under the strip, when "Details" is open. */
const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.25rem;
  border: 1px solid var(--surface-border);
  border-top: 0;
  border-radius: 0 0 8px 8px;
  background: var(--surface-card);
`;

/**
 * The pipeline beside the AI's words when there is room for both (about 45rem — it sits in the wizard
 * column, whose width depends on the conversation beside it), one above the other when there is not.
 */
const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(22rem, 1fr));
  gap: 1.5rem;
  align-items: start;
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
`;

const Tip = styled.p`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin: 0;
  padding-top: 0.75rem;
  border-top: 1px solid var(--surface-border);
  font-size: 0.8rem;
  color: var(--text-color-secondary);

  span {
    animation: ${fadeIn} 0.5s ease-out;
  }
`;

export type GenerationProgressRun = Pick<
  IDraftRunDetail,
  | "status"
  | "stage"
  | "updatedAt"
  | "startedAt"
  | "progress"
  | "typicalDurationMs"
  | "documents"
  | "versionNo"
>;

interface IGenerationProgressProps {
  run: GenerationProgressRun;
  /** Cancel the model (the page's confirm dialog). Offered while queued or running. */
  onStop?: () => void;
  stopping?: boolean;
}

function GenerationProgress({
  run,
  onStop,
  stopping,
}: IGenerationProgressProps) {
  // Tick every second: the clocks, the eased bar and the tips are live. The data itself arrives with the
  // page's poll; this only re-renders this card.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // What this page has seen of the pass so far, across polls. Folded in during render: each fold is
  // idempotent (latest-wins starts, max for the bar, union for the ticks), so a repeated render is harmless.
  const passKey = passKeyOf(run);
  const observed = useRef<{ key: number; obs: IPhaseObservations }>({
    key: run.versionNo,
    obs: EMPTY_OBSERVATIONS,
  });
  const shownPercent = useRef<IMonotonicPercent | null>(null);
  const returnedSeen = useRef<{ key: string; at: number } | null>(null);
  const ticks = useRef<{ key: string; ids: string[] }>({ key: "", ids: [] });

  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  // The worker clears its progress at deposit; keep its last report for the seconds of worker_returned.
  const progress = useLiveProgress(run);

  const { status, stage } = run;
  const active = activePhaseOf(status, progress);
  if (!active) return null;

  if (observed.current.key !== run.versionNo)
    observed.current = { key: run.versionNo, obs: EMPTY_OBSERVATIONS };
  observed.current.obs = observeProgress(observed.current.obs, {
    status,
    progress,
    updatedAt: run.updatedAt,
  });
  const { obs } = observed.current;

  if (status === "worker_returned") {
    if (returnedSeen.current?.key !== passKey)
      returnedSeen.current = { key: passKey, at: now };
  }
  const returnedSeenAtMs =
    returnedSeen.current?.key === passKey ? returnedSeen.current.at : null;

  const documents = mergeDocuments(run.documents, status, progress);
  const raw = overallPercent({
    status,
    progress,
    documents,
    typicalDurationMs: run.typicalDurationMs,
    nowMs: now,
    queuedAtMs: obs.queuedAtMs,
    returnedSeenAtMs,
  });
  shownPercent.current = holdMonotonic(shownPercent.current, passKey, raw);

  const items = checklistFor(stage);
  const narration = narrationOf(progress);
  const ticksKey = `${run.versionNo}:${stage}`;
  if (ticks.current.key !== ticksKey)
    ticks.current = { key: ticksKey, ids: [] };
  ticks.current.ids = accumulateTicks(ticks.current.ids, items, narration);

  const elapsedMs = generationElapsedMs(run, now);
  const typical = compactDuration(run.typicalDurationMs);
  const activeStart = activePhaseStartMs({
    active,
    observations: obs,
    progress,
    startedAt: run.startedAt,
    returnedSeenAtMs,
  });
  const thinkingStart =
    progress?.phase === "thinking"
      ? phaseStartMs(progress)
      : obs.starts.thinking ?? null;

  // The strip's spinner already says "in progress"; the chip is for the two states it does not.
  let chip: { label: string; severity?: "info" | "success" } | null = null;
  if (status === "queued")
    chip = { label: PROGRESS_CHIP_QUEUED, severity: "info" };
  if (active === "saving")
    chip = { label: PROGRESS_CHIP_SAVING, severity: "success" };

  const canStop = !!onStop && (status === "queued" || status === "running");

  return (
    <div className="mb-3">
      <ProgressStrip
        title={stage === "facts" ? PROGRESS_TITLE_FACTS : PROGRESS_TITLE_FULL}
        chip={chip}
        elapsedMs={elapsedMs}
        typical={typical ? usuallyAbout(typical) : null}
        percent={shownPercent.current.value}
        now={describeProgress(status, progress, now, stage)}
        slow={isTakingLongerThanUsual(elapsedMs, run.typicalDurationMs)}
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        onStop={canStop ? onStop : undefined}
        stopping={stopping}
        detailsId={detailsId}
      />

      {expanded ? (
        <Details id={detailsId}>
          <PhaseTimeline
            active={active}
            durations={derivePhaseDurations({
              active,
              observations: obs,
              progress,
              startedAt: run.startedAt,
              updatedAt: run.updatedAt,
            })}
            activeElapsedMs={
              activeStart == null ? null : Math.max(0, now - activeStart)
            }
            reused={obs.reused || isReusePass(progress)}
          />
          <Columns>
            <Left>
              <DocumentList rows={documents} />
              <LookingForChecklist items={items} ticked={ticks.current.ids} />
            </Left>
            <LiveFromAi
              active={active}
              narration={narration}
              activity={activityOf(progress)}
              model={progress?.model ?? null}
              turns={turnsOf(progress)}
              thinkingElapsedMs={
                thinkingStart == null ? null : Math.max(0, now - thinkingStart)
              }
            />
          </Columns>
          <Tip>
            <i className="pi pi-info-circle" />
            <span key={tipAt(now)}>{tipAt(now)}</span>
          </Tip>
        </Details>
      ) : null}
    </div>
  );
}

export default GenerationProgress;
